// AddEdit.tsx - UPDATED VERSION
import {
  FormDatePicker,
  FormFileInput,
  FormInput,
  FormSelect,
  FormTextarea,
} from "@/components";
import useAddEdit from "./useAddEdit";
import { FormProvider } from "react-hook-form";
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui";
import {
  INVESTOR_TYPE,
  SUB_TYPE,
  TYPE,
  TITLE,
  DATE,
  YEAR,
  DESCRIPTION,
  UPLOAD,
  RATING_TYPE,
  RATING_NAME,
  RATING_DESCRIPTION,
  ADDITIONAL_UPLOAD,
  INVESTOR_TYPES,
  INVESTOR_SUB_TYPES_BY_TYPE,
  TYPES,
  InvestorType,
  THUMBNAIL_UPLOAD,
  UPLOAD_TRANSCRIPT,
  UPLOAD_LOGO,
  LINK,
} from "./consts";
import { Trash2, X, Plus } from "lucide-react";
import { capitalize } from "@/lib/utils";
import { Dialog, Loader, ProgressBar } from "@/components"; // Import ProgressBar
import { useEffect } from "react";

const AddEdit = () => {
  const {
    form,
    onSubmit,
    isView,
    onCancel,
    isMobile,
    action,
    isEdit,
    uploadRef: mainUploadRef,
    thumbnailUploadRef,
    addAdditionalUpload,
    removeAdditionalUpload,
    fieldConfig,
    watchedValues,
    isExistingDataLoading,
    isInitialized,
    onViewImage,
    onFileChange,
    showLink,
    openImageViewer,
    setOpenImageViewer,
    imageSrc,
    isImageLoading,
    viewAdditionalFile,
    isViewingVideo,
    onViewThumbnail,
    isViewingPDF,
    onViewTranscript,
    uploadTranscriptRef,
    onViewLogo,
    uploadLogoRef,
    // New progress states
    uploadProgress,
    uploadingFiles,
  } = useAddEdit();

  const { errors } = form.formState;
  const { subType } = watchedValues;

  const dateValue = form.watch(DATE);

  useEffect(() => {
    if (dateValue) {
      const date = new Date(dateValue);
      const year = date.getFullYear();
      const month = date.getMonth(); // 0 = Jan, 3 = Apr

      // If month is before April (Jan-Mar), FY belongs to previous year
      const fyStart = month < 3 ? year - 1 : year;
      const fyEnd = fyStart + 1;

      const fy = `FY ${fyStart}-${fyEnd}`;
      form.setValue(YEAR, fy);
    }
  }, [dateValue, form]);

  // Filter types based on selected subType
  const getFilteredTypes = () => {
    if (!subType) return TYPES;

    const typeMap: { [key: string]: string[] } = {
      "Investors Presentation & Press Release": [
        "Investors Presentation",
        "Press Release",
      ],
      "Quarterly Results": ["Quarterly Results"],
      "Annual Reports": ["Annual Reports"],
      "Annual Return": ["Annual Return"],
      "Webcast & Transcripts": ["Webcast & Transcripts"],
      "Credit Ratings": ["Credit Ratings"],
      "Policies & Code of Conduct": ["Policies & Code of Conduct"],
      "Corporate Governance": ["Corporate Governance"],
      "Advertisements in Newspapers as per Regulation 47(1)": [
        "Advertisements in Newspapers as per Regulation 47(1)",
      ],
      "Disclosure under Regulation 46 and 62 of SEBI (LODR)": [
        "Disclosure under Regulation 46 and 62 of SEBI (LODR)",
        "Memorandum of Association and Articles of Association",
      ],
      "Notices & Disclosures": ["Notices", "Disclosure of Material Events"],
      "Related Party Transactions": ["Related Party Transactions"],
      "Unclaimed Dividend": ["Unclaimed Dividend"],
      "Shareholding Pattern": ["Shareholding Pattern"],
      "Secretarial Compliance Report": ["Secretarial Compliance Report"],
    };

    const allowedTypes = typeMap[subType] || [];
    return TYPES.filter((t) => allowedTypes.includes(t.value));
  };

  const shouldShowField = (fieldName: string) => {
    return fieldConfig?.fields?.includes(fieldName);
  };

  const hasAdditionalUpload = fieldConfig?.additionalUpload;

  const investorTypeWatch = form.watch(INVESTOR_TYPE) as
    | InvestorType
    | undefined;

  const subTypeOptions = investorTypeWatch
    ? INVESTOR_SUB_TYPES_BY_TYPE[investorTypeWatch]
    : [];

  // Check if any file is currently uploading
  const isAnyFileUploading = Object.values(uploadingFiles).some(value => value);

  // In AddEdit.tsx, replace the loading check with:
  if ((isEdit || isView) && (isExistingDataLoading || !isInitialized)) {
    return (
      <Drawer open={true} direction={isMobile ? "bottom" : "right"}>
        <DrawerContent className="px-6 lg:min-w-[500px] xl:min-w-[730px]">
          <div className="flex items-center justify-center h-40">
            <p>Loading form data...</p>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <>
      <Drawer open={true} direction={isMobile ? "bottom" : "right"}>
        <DrawerContent className="px-6 lg:min-w-[500px] xl:min-w-[730px]">
          <DrawerHeader className="px-0">
            <div className="flex items-center justify-between">
              <DrawerTitle className="text-[20px]">
                {capitalize(action || "")} Investor Information
              </DrawerTitle>
              <X
                onClick={onCancel}
                className="cursor-pointer stroke-g-300 hover:stroke-g-400"
              />
            </div>
          </DrawerHeader>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
              <div className="bg-white rounded-lg py-[25px] max-h-[70vh] lg:max-h-[95vh]  overflow-y-auto pb-[120px] px-1">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[25px]">
                  {/* Common Fields - Always Visible */}
                  <FormSelect
                    name={INVESTOR_TYPE}
                    control={form.control}
                    label="Investor Type"
                    placeholder="Please select"
                    error={errors[INVESTOR_TYPE]?.message}
                    isRequired={true}
                    disabled={isView}
                    options={INVESTOR_TYPES}
                  />

                  <FormSelect
                    name={SUB_TYPE}
                    control={form.control}
                    label="Sub Type"
                    placeholder="Please select"
                    error={errors[SUB_TYPE]?.message}
                    isRequired={true}
                    disabled={isView}
                    options={subTypeOptions}
                  />

                  <FormSelect
                    name={TYPE}
                    control={form.control}
                    label="Type"
                    placeholder="Please select"
                    error={errors[TYPE]?.message}
                    isRequired={true}
                    disabled={isView}
                    options={getFilteredTypes()}
                  />

                  <div className="md:col-span-2 lg:col-span-3">
                    <FormInput
                      name={TITLE}
                      control={form.control}
                      label="Title"
                      placeholder="Please enter"
                      error={errors[TITLE]?.message}
                      isRequired={true}
                      disabled={isView}
                    />
                  </div>

                  {/* Conditionally Rendered Fields */}
                  {fieldConfig && (
                    <>
                      {shouldShowField(DATE) && (
                        <FormDatePicker
                          name={DATE}
                          control={form.control}
                          label="Date"
                          placeholder="Please select"
                          disabled={isView}
                          isRequired={true}
                          error={errors[DATE]?.message}
                        />
                      )}
                      {shouldShowField(YEAR) && (
                        <FormInput
                          name={YEAR}
                          control={form.control}
                          label="Year"
                          placeholder="Please enter"
                          error={errors[YEAR]?.message}
                          isRequired={true}
                          disabled={true}
                        />
                      )}
                      {shouldShowField(DESCRIPTION) && (
                        <div className="md:col-span-2 lg:col-span-3">
                          <FormTextarea
                            name={DESCRIPTION}
                            control={form.control}
                            label="Transcript Description"
                            placeholder="Please enter"
                            disabled={isView}
                            isRequired={true}
                            error={errors[DESCRIPTION]?.message}
                          />
                        </div>
                      )}
                      {shouldShowField(THUMBNAIL_UPLOAD) && (
                        <div className="md:col-span-2 lg:col-span-3">
                          <FormFileInput
                            fileInputRef={thumbnailUploadRef}
                            name={THUMBNAIL_UPLOAD}
                            control={form.control}
                            label="Thumbnail Upload (dimension 600x350)"
                            placeholder="Please enter"
                            error={errors[THUMBNAIL_UPLOAD]?.message}
                            isRequired
                            disabled={isView || uploadingFiles.thumbnail}
                            accept="image/jpeg,image/png,image/jpg,mp4"
                            minSize={500}
                            maxSize={600}
                            uploadFileErrorMsg="File size should be between 500 KB and 600 KB (JPEG, JPG, PNG)"
                            onChange={onFileChange}
                          />
                          {/* Thumbnail Upload Progress Bar */}
                          {uploadingFiles.thumbnail && (
                            <div className="mt-2">
                              <ProgressBar progress={uploadProgress.thumbnail || 0} />
                            </div>
                          )}
                          {showLink && !uploadingFiles.thumbnail ? (
                            <Button
                              type="button"
                              variant="link"
                              onClick={onViewThumbnail}
                            >
                              View Image
                            </Button>
                          ) : null}
                        </div>
                      )}

                      {shouldShowField(UPLOAD_TRANSCRIPT) && (
                        <div className="md:col-span-2 lg:col-span-3">
                          <FormFileInput
                            fileInputRef={uploadTranscriptRef}
                            name={UPLOAD_TRANSCRIPT}
                            control={form.control}
                            label="Transcript Upload"
                            placeholder="Please upload transcript file"
                            error={errors[UPLOAD_TRANSCRIPT]?.message}
                            isRequired={true}
                            disabled={isView || uploadingFiles.transcript}
                            accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            maxSize={10000}
                            uploadFileErrorMsg="File size should be less than 10MB (PDF, DOC, DOCX, TXT)"
                            onChange={onFileChange}
                          />
                          {/* Transcript Upload Progress Bar */}
                          {uploadingFiles.transcript && (
                            <div className="mt-2">
                              <ProgressBar progress={uploadProgress.transcript || 0} />
                            </div>
                          )}
                          {showLink && !uploadingFiles.transcript ? (
                            <Button
                              type="button"
                              variant="link"
                              onClick={onViewTranscript}
                            >
                              View Transcript
                            </Button>
                          ) : null}
                        </div>
                      )}
                      {shouldShowField(LINK) && !(form.watch(UPLOAD)?.attachmentId) && (
                        <FormInput
                          name={LINK}
                          control={form.control}
                          label="Link"
                          placeholder="Enter Link"
                          disabled={isView}
                        />
                      )}

                      {shouldShowField(UPLOAD_LOGO) && (
                        <div className="md:col-span-2 lg:col-span-3">
                          <FormFileInput
                            fileInputRef={uploadLogoRef}
                            name={UPLOAD_LOGO}
                            control={form.control}
                            label="Logo Upload"
                            placeholder="Please upload logo file"
                            error={errors[UPLOAD_LOGO]?.message}
                            isRequired={true}
                            disabled={isView || uploadingFiles.logo}
                            accept="image/jpeg,image/png,image/jpg,mp4"
                            maxSize={10000}
                            uploadFileErrorMsg="Image size should be less than 10MB (JPEG, PNG, JPG)"
                            onChange={(file: File) => onFileChange(file, UPLOAD_LOGO)}
                          />
                          {/* Logo Upload Progress Bar */}
                          {uploadingFiles.logo && (
                            <div className="mt-2">
                              <ProgressBar progress={uploadProgress.logo || 0} />
                            </div>
                          )}
                          {showLink && !uploadingFiles.logo ? (
                            <Button
                              type="button"
                              variant="link"
                              onClick={onViewLogo}
                            >
                              View Logo
                            </Button>
                          ) : null}
                        </div>
                      )}

                      {shouldShowField(UPLOAD) && !(form.watch(LINK)?.trim?.()) && (
                        <div className="md:col-span-2 lg:col-span-3">
                          <FormFileInput
                            fileInputRef={mainUploadRef}
                            name={UPLOAD}
                            control={form.control}
                            label="Upload"
                            placeholder={
                              fieldConfig?.uploadAccept === "video/mp4"
                                ? "Please upload MP4 video"
                                : "Please upload file"
                            }
                            error={errors[UPLOAD]?.message}
                            isRequired
                            disabled={isView || uploadingFiles.main}
                            accept={
                              fieldConfig?.uploadAccept === "video/mp4"
                                ? "video/mp4,video/webm,video/ogg"
                                : "image/jpeg,image/png,image/jpg,application/pdf"
                            }
                            maxSize={
                              fieldConfig?.uploadAccept === "video/mp4"
                                ? 102400
                                : 100000
                            }
                            uploadFileErrorMsg={
                              fieldConfig?.uploadAccept === "video/mp4"
                                ? "Video files (MP4, WebM, OGG) are allowed, max 100MB"
                                : "Only JPG, PNG, PDF allowed(should be less than 300KB)"
                            }
                            onChange={onFileChange}
                          />
                          {/* Main File Upload Progress Bar */}
                          {uploadingFiles.main && (
                            <div className="mt-2">
                              <ProgressBar progress={uploadProgress.main || 0} />
                            </div>
                          )}
                          {showLink && !uploadingFiles.main ? (
                            <Button
                              type="button"
                              variant="link"
                              onClick={onViewImage}
                            >
                              {fieldConfig?.uploadAccept === "video/mp4"
                                ? "View Video"
                                : "View File"}
                            </Button>
                          ) : null}
                        </div>
                      )}
                      {/* Credit Rating Specific Fields */}
                      {shouldShowField(RATING_TYPE) && (
                        <FormInput
                          name={RATING_TYPE}
                          control={form.control}
                          label="Rating Type"
                          placeholder="Please enter"
                          error={errors[RATING_TYPE]?.message}
                          isRequired={true}
                          disabled={isView}
                        />
                      )}
                      {shouldShowField(RATING_NAME) && (
                        <FormInput
                          name={RATING_NAME}
                          control={form.control}
                          label="Rating Name"
                          placeholder="Please enter"
                          error={errors[RATING_NAME]?.message}
                          isRequired={true}
                          disabled={isView}
                        />
                      )}
                      {shouldShowField(RATING_DESCRIPTION) && (
                        <div className="md:col-span-2 lg:col-span-3">
                          <FormTextarea
                            name={RATING_DESCRIPTION}
                            control={form.control}
                            label="Rating Description"
                            placeholder="Please enter"
                            disabled={isView}
                            isRequired={true}
                            error={errors[RATING_DESCRIPTION]?.message}
                          />
                        </div>
                      )}
                      {/* Additional Upload Section */}
                      {hasAdditionalUpload && (
                        <div className="md:col-span-2 lg:col-span-3 border-t pt-4">
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">
                              Additional Uploads
                            </h3>
                            {!isView && (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={addAdditionalUpload}
                                className="flex items-center gap-2"
                                disabled={isAnyFileUploading}
                              >
                                <Plus size={16} />
                                Add Upload
                              </Button>
                            )}
                          </div>

                          {form
                            .watch(ADDITIONAL_UPLOAD)
                            ?.map((upload, index) => (
                              <div
                                key={index}
                                className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg mb-4 bg-gray-50"
                              >
                                <FormInput
                                  name={`${ADDITIONAL_UPLOAD}.${index}.subTitle`}
                                  control={form.control}
                                  label="Sub Title"
                                  placeholder="Optional description"
                                  disabled={isView || uploadingFiles[`subtitle-${index}`]}
                                />

                                <div className="flex flex-col gap-2">
                                  <FormFileInput
                                    fileInputRef={mainUploadRef}
                                    name={`${ADDITIONAL_UPLOAD}.${index}.file`}
                                    control={form.control}
                                    label="File"
                                    maxSize={100000}
                                    uploadFileErrorMsg={"Only JPG, PNG, PDF allowed(should be less than 300KB)"}
                                    disabled={isView || uploadingFiles[`subtitle-${index}`]}
                                    isRequired={true}
                                  />
                                  {/* Additional File Upload Progress Bar */}
                                  {uploadingFiles[`subtitle-${index}`] && (
                                    <div className="mt-2">
                                      <ProgressBar progress={uploadProgress[`subtitle-${index}`] || 0} />
                                    </div>
                                  )}
                                  {upload.file &&
                                    typeof upload.file === "object" &&
                                    "attachmentId" in upload.file &&
                                    !uploadingFiles[`subtitle-${index}`] && (
                                      <Button
                                        type="button"
                                        variant="link"
                                        onClick={() =>
                                          viewAdditionalFile(
                                            upload.file.attachmentId
                                          )
                                        }
                                        className="text-left p-0 h-auto text-sm"
                                      >
                                        View File
                                      </Button>
                                    )}
                                </div>

                                {!isView && !uploadingFiles[`subtitle-${index}`] && (
                                  <div className="md:col-span-2 flex justify-end">
                                    <Button
                                      type="button"
                                      variant="destructive"
                                      onClick={() =>
                                        removeAdditionalUpload(index)
                                      }
                                      className="flex items-center gap-2"
                                    >
                                      <Trash2 size={16} />
                                      Remove
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-row justify-end gap-3 mt-15 py-3 absolute bottom-5 w-full bg-white pb-[25px] z-[3]">
                <Button 
                  variant="outline" 
                  type="button" 
                  onClick={onCancel}
                  disabled={isAnyFileUploading}
                >
                  Cancel
                </Button>
                {isView ? null : (
                  <Button 
                    type="submit"
                    disabled={isAnyFileUploading}
                  >
                    {isEdit ? "Update" : "Submit"}
                  </Button>
                )}
              </div>
            </form>
          </FormProvider>
        </DrawerContent>
      </Drawer>

      <Dialog
        open={openImageViewer}
        setOpen={setOpenImageViewer}
        title={
          <h2 className="text-2xl font-bold">
            {isViewingVideo ? "View Video" : 
            isViewingPDF ? "View PDF" : 
            "View File"}
          </h2>
        }
        hideFooter
        contentClassName="max-h-[90vh] max-w-[90vw] overflow-auto"
      >
        <div className="relative bg-white flex items-center justify-center">
          {isImageLoading ? (
            <Loader />
          ) : (
            <>
              {isViewingVideo ? (
                <video
                  controls
                  className="max-h-[85vh] max-w-[85vw] object-contain"
                  autoPlay
                >
                  <source src={imageSrc} type="video/mp4" />
                  <source src={imageSrc} type="video/webm" />
                  <source src={imageSrc} type="video/ogg" />
                </video>
              ) : isViewingPDF ? (
                <iframe
                  src={imageSrc}
                  className="w-[85vw] h-[85vh] border-0"
                  title="PDF Viewer"
                />
              ) : (
                imageSrc?.endsWith?.('.pdf') || 
                imageSrc?.includes?.('application/pdf') ? (
                  <iframe
                    src={imageSrc}
                    className="w-[85vw] h-[85vh] border-0"
                    title="Transcript Viewer"
                  />
                ) : (
                  <img
                    src={imageSrc}
                    className="max-h-[85vh] max-w-[85vw] object-contain"
                    alt="Preview"
                  />
                )
              )}
            </>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default AddEdit;